import type { TemplateResult } from 'lit';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseListSectionElement } from 'src/library/lists/base-list-section-element.js';
import '../elements/d-label.js';
import '../elements/d-action';
import { DListSection } from './d-list-section.js';
import './d-list-section-item.js';
import { uuid } from 'src/utilities/text';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { promo } from 'src/store/selectors/promo';
import { DPromoDialog } from 'src/library/promo/d-promo-dialog';
import { features } from '../../store/selectors/features/features';
import type { OrganizationViewModelSpecialTermsEnum } from '@trinnvis/dabih-api-client';
import type { ListSectionItemInput } from './listSectionItemInput';
import type { CreateRiskAssessmentInput } from '../../layout/parts/create-risk-assessment-input';
import type { CreateEntityInput } from '../../layout/parts/create-entity-input';
import type { ActionInput } from '../elements/action-input';
import { t } from 'src/i18n';

/**
 *
 * This list is used to show related risk assessments for substances. To extend to other entity types requires check of assumptions in this element.
 *
 */
@customElement('d-list-section-related-risk-assessments')
export class DListSectionRelatedRiskAssessments extends BaseListSectionElement<ListSectionItemInput> {
  static readonly styles = [
    ...DListSection.styles,
    css`
      :host {
        display: block;
        margin-bottom: 6px;
      }
      d-action {
        margin: -6px -8px;
      }
    `,
  ];
  @property({ type: String })
  field = '';
  @property({ type: String })
  relatedItemType: 'substance' | 'dataItem' = 'substance';
  @property({ type: String })
  relatedItemUuid = '';
  @property({ type: String })
  relatedItemHref = '';
  @property({ type: Boolean })
  disabled = false;
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Number })
  employeesCount = 0;
  @property({ type: String })
  specialTerms: OrganizationViewModelSpecialTermsEnum | undefined = undefined;
  @property({ type: Boolean, reflect: true, attribute: 'theme-page' })
  themePage = false;
  @property({ type: Boolean, attribute: 'write-access', reflect: true })
  writeAccess = false;

  private get actions(): ActionInput[] {
    return [
      {
        name: t('riskAssessments.add'),
        action: 'new',
        disabled: this.disabled,
        slot: 'top-right',
      },
    ];
  }

  render() {
    return html`
      <d-list-section
        field="${this.field}"
        label="${t('riskAssessments.sectionLabel')}"
        icon="riskAssessments"
        .items=${this.items}
        ?theme-page=${this.themePage}
        .actions=${this.actions}
        no-item-text="${t('riskAssessments.noRiskAssessments')}"
        .contentStickyTop=${this.contentStickyTop}
        @action=${this.onAction}
      >
        ${this.disabled
          ? html` <slot name="after-header-label" slot="after-header-label">
              <div class="module-message">
                ${t('riskAssessments.requiresModule')}
                <d-action plain @click=${this.onPromoClick}>${t('features.risk.displayName')}</d-action>
              </div>
            </slot>`
          : nothing}
      </d-list-section>
    `;
  }

  renderItemsEmpty(): TemplateResult | typeof nothing {
    return html`<d-list-section-item label="${t('riskAssessments.noRiskAssessments')}" class="noItems">
    </d-list-section-item>`;
  }

  private async onPromoClick() {
    const promoData = promo.find((p) => {
      return p.id === 'risk';
    });
    if (promoData) {
      await DPromoDialog.open({
        promoData,
        features: features,
        employeesCount: this.employeesCount,
        specialTerms: this.specialTerms,
        healthcareSignatoryOptions: [],
        healthcareSignatory: undefined,
        canUpgrade: this.writeAccess,
        hasHealthcare: false,
      });
    }
  }

  private onAction(e: CustomEvent<string>) {
    if (e.detail === 'new') {
      if (this.relatedItemType === 'substance') {
        this.createForSubstance();
      } else if (this.relatedItemType === 'dataItem') {
        this.createForDataItem();
      }
    }
  }

  private createForSubstance() {
    const id = uuid();
    const input: CreateRiskAssessmentInput = {
      entityType: 'riskAssessments',
      entityUuid: id,
      pageId: pageIds.riskAssessments,
      targetUrl: this.relatedItemHref + '/riskAssessments/' + id + '?edit',

      substanceUuid: this.relatedItemUuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }

  private createForDataItem() {
    const id = uuid();
    const input: CreateRiskAssessmentInput = {
      entityType: 'riskAssessments',
      entityUuid: id,
      pageId: pageIds.informationSecurity,
      targetUrl: this.relatedItemHref + '/riskAssessments/' + id + '?edit',

      dataItemUuid: this.relatedItemUuid,
    };
    this.dispatchEvent(
      new CustomEvent<CreateEntityInput>('create-entity', {
        bubbles: true,
        composed: true,
        detail: input,
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-list-section-related-risk-assessments': DListSectionRelatedRiskAssessments;
  }
}
